import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Box, IconButton, MenuItem, Chip } from '@mui/material';
import { CrewField } from '../RaptorJobCreateModal';
import Autocomplete from '@/common/presentation/components/inputs/Autocomplete';
import Select from '@/common/presentation/components/inputs/Select';
import tid from '@/common/application/utils/tid';
import { CrewAssignmentStatus } from '@/common/infrastructure/gql/generated/schema';
import { useCrewSelector } from '../hook/useCrewSelector';

// Since we are supporting ERP Jobs and Raptor Jobs, we need to filter the roles supported by Raptor Jobs.

interface CrewSelectorProps {
  crewFields: CrewField[];
  setCrewFields: (fields: CrewField[]) => void;
  recurrence: 'single' | 'multi';
  startDate?: Date | null;
  endDate?: Date | null;
}

export const CrewSelector = ({ crewFields, setCrewFields, recurrence, startDate, endDate }: CrewSelectorProps) => {
  const {
    loadingJobRoles,
    setEmployeeSearchTerm,
    loadingEmployees,
    buildEmployeeOptionsForRole,
    handleDeleteCrewField,
    handleRoleChange,
    handleEmployeeSelect,
    supportedJobRoles
  } = useCrewSelector({ crewFields, setCrewFields, recurrence, startDate, endDate });

  return (
    <>
      {crewFields.map((crew) => (
        <Box key={crew.rowId} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Select
            fullWidth
            value={crew.roleId ?? ''}
            label="Role"
            disabled={loadingJobRoles}
            onChange={(e) => {
              const selectedRole = supportedJobRoles?.find((role) => role.id === Number(e.target.value));
              if (selectedRole) handleRoleChange(crew.rowId, selectedRole.id, selectedRole.name || '');
            }}
            {...tid('jobs:create:crew-role-select')}
          >
            {(supportedJobRoles || []).map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
          <Autocomplete
            fullWidth
            options={buildEmployeeOptionsForRole(crew.roleId)}
            loading={crew.roleId != null && loadingEmployees}
            optionsloading={crew.roleId != null && loadingEmployees}
            disabled={crew.roleId == null}
            value={buildEmployeeOptionsForRole(crew.roleId).find((option) => option.id === crew.employeeId) || null}
            onChange={(_, newValue) => {
              const selectedValue = Array.isArray(newValue) ? newValue[0] : newValue;
              handleEmployeeSelect(crew.rowId, selectedValue?.id || null, selectedValue?.name || '', selectedValue?.status);
            }}
            onInputChange={(_, inputValue, reason) => {
              if (crew.roleId == null) return; // Ignore typing until a role is selected
              if (reason === 'input') setEmployeeSearchTerm(inputValue);
              else if (reason === 'clear') setEmployeeSearchTerm('');
            }}
            clearOnBlur={false}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            placeholder={crew.roleId != null ? 'Employee' : 'Select a Role first'}
            label="Employee"
            sx={{ width: '100%' }}
            renderOption={(props, option) => {
              const isBanned = option.status?.includes(CrewAssignmentStatus.Banned);
              const isOutOfOffice = option.status?.includes(CrewAssignmentStatus.OutOfOffice);

              return (
                <Box component="li" {...props} key={option.id}>
                  <Box component="span">{option.name}</Box>
                  {isBanned && (
                    <Chip
                      label="BANNED"
                      size="small"
                      sx={{
                        backgroundColor: 'error.main',
                        color: 'common.white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: '20px'
                      }}
                    />
                  )}
                  {isOutOfOffice && !isBanned && (
                    <Chip
                      label="OUT OF OFFICE"
                      size="small"
                      sx={{
                        backgroundColor: 'warning.main',
                        color: 'common.white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: '20px'
                      }}
                    />
                  )}
                </Box>
              );
            }}
            {...tid('jobs:create:crew-autocomplete')}
          />

          <IconButton onClick={() => handleDeleteCrewField(crew.rowId)} sx={{ color: 'error.main' }}>
            <DeleteOutlinedIcon />
          </IconButton>
        </Box>
      ))}
    </>
  );
};
